/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Chip,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { apiService, Coupon, UserCoupon } from '../services/api';
import CustomerHeader from '../components/CustomerHeader';
import './CouponsPage.css';

const CouponsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [availableCoupons, setAvailableCoupons] = useState<Coupon[]>([]);
  const [userCoupons, setUserCoupons] = useState<UserCoupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clippingCoupon, setClippingCoupon] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'available' | 'my-coupons'>('available');

  useEffect(() => {
    loadCoupons();
  }, [user]);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load available coupons
      const coupons = await apiService.getAvailableCoupons({
        user_id: user?.id,
      });
      setAvailableCoupons(coupons);

      // Load user's clipped coupons if authenticated
      if (isAuthenticated && user) {
        try {
          const clipped = await apiService.getUserCoupons(user.id);
          setUserCoupons(clipped);
        } catch (err) {
          console.warn('Failed to load user coupons:', err);
          // Don't show error, just continue without user coupons
        }
      }
    } catch (err: any) {
      console.error('Error loading coupons:', err);
      setError(err.message || 'Failed to load coupons. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClipCoupon = async (couponId: string) => {
    if (!isAuthenticated || !user) {
      navigate('/login', { state: { returnTo: '/coupons' } });
      return;
    }

    try {
      setClippingCoupon(couponId);
      await apiService.clipCoupon(couponId, user.id);
      // Reload user coupons
      const clipped = await apiService.getUserCoupons(user.id);
      setUserCoupons(clipped);
      // Show success message
      alert('Coupon clipped successfully!');
    } catch (err: any) {
      console.error('Error clipping coupon:', err);
      alert(err.message || 'Failed to clip coupon. Please try again.');
    } finally {
      setClippingCoupon(null);
    }
  };

  const handleRemoveCoupon = async (couponId: string) => {
    if (!isAuthenticated || !user) return;

    try {
      setClippingCoupon(couponId);
      await apiService.removeClippedCoupon(couponId, user.id);
      // Reload user coupons
      const clipped = await apiService.getUserCoupons(user.id);
      setUserCoupons(clipped);
    } catch (err: any) {
      console.error('Error removing coupon:', err);
      alert(err.message || 'Failed to remove coupon. Please try again.');
    } finally {
      setClippingCoupon(null);
    }
  };

  const formatDiscount = (coupon: Coupon): string => {
    if (coupon.discount_type === 'percentage') {
      return `${coupon.discount_value}% OFF`;
    } else if (coupon.discount_type === 'fixed') {
      return `₹${coupon.discount_value} OFF`;
    } else {
      return 'FREE SHIPPING';
    }
  };

  const isCouponClipped = (couponId: string): boolean => {
    return userCoupons.some(uc => uc.coupon_id === couponId);
  };

  const isCouponExpired = (coupon: Coupon | undefined): boolean => {
    if (!coupon || !coupon.valid_until) return false;
    return new Date(coupon.valid_until) < new Date();
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const renderCouponCard = (coupon: Coupon | undefined, isClipped: boolean = false) => {
    // Handle undefined coupon
    if (!coupon) {
      return (
        <Card className="coupon-page-card" sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary">
              Coupon data unavailable
            </Typography>
          </CardContent>
        </Card>
      );
    }

    const expired = isCouponExpired(coupon);
    const isClipping = clippingCoupon === coupon.id;

    return (
      <Card 
        key={coupon.id} 
        className={`coupon-page-card ${expired ? 'expired' : ''} ${isClipped ? 'clipped' : ''}`}
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          opacity: expired ? 0.6 : 1,
        }}
      >
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          {expired && (
            <Chip 
              label="Expired" 
              size="small" 
              color="error" 
              sx={{ mb: 1, alignSelf: 'flex-start' }}
            />
          )}
          {isClipped && !expired && (
            <Chip 
              label="Clipped" 
              size="small" 
              color="success" 
              sx={{ mb: 1, alignSelf: 'flex-start' }}
            />
          )}
          
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700, 
              color: '#2563eb', 
              mb: 1,
              fontSize: '2rem',
            }}
          >
            {formatDiscount(coupon)}
          </Typography>

          {coupon.title && (
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1f2937' }}>
              {coupon.title}
            </Typography>
          )}

          {coupon.description && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#6b7280', 
                mb: 2,
                flexGrow: 1,
              }}
            >
              {coupon.description}
            </Typography>
          )}

          {coupon.minimum_order_amount && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#6b7280', 
                mb: 1,
                fontSize: '0.875rem',
              }}
            >
              Min. order: ₹{coupon.minimum_order_amount}
            </Typography>
          )}

          <Box sx={{ mt: 'auto', pt: 2 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#6b7280', 
                mb: 2,
                fontSize: '0.875rem',
              }}
            >
              Valid until: {formatDate(coupon.valid_until)}
            </Typography>

            {activeTab === 'available' && !isClipped && !expired && (
              <Button
                variant="contained"
                fullWidth
                onClick={() => handleClipCoupon(coupon.id)}
                disabled={isClipping}
                sx={{
                  backgroundColor: '#2563eb',
                  '&:hover': { backgroundColor: '#1e40af' },
                  textTransform: 'none',
                }}
              >
                {isClipping ? <CircularProgress size={20} /> : 'Clip Coupon'}
              </Button>
            )}

            {activeTab === 'my-coupons' && isClipped && (
              <Button
                variant="outlined"
                fullWidth
                onClick={() => handleRemoveCoupon(coupon.id)}
                disabled={isClipping}
                sx={{
                  borderColor: '#dc2626',
                  color: '#dc2626',
                  '&:hover': { 
                    borderColor: '#b91c1c',
                    backgroundColor: '#fef2f2',
                  },
                  textTransform: 'none',
                }}
              >
                {isClipping ? <CircularProgress size={20} /> : 'Remove'}
              </Button>
            )}

            {expired && (
              <Button
                variant="outlined"
                fullWidth
                disabled
                sx={{
                  textTransform: 'none',
                }}
              >
                Expired
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="coupons-page">
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton
        onBackClick={() => navigate(-1)}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/cart')}
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            mb: 4,
            color: '#1f2937',
          }}
        >
          Coupons & Offers
        </Typography>

        {/* Tabs */}
        <Box sx={{ mb: 4, borderBottom: 1, borderColor: 'divider' }}>
          <Button
            onClick={() => setActiveTab('available')}
            sx={{
              textTransform: 'none',
              color: activeTab === 'available' ? '#2563eb' : '#6b7280',
              borderBottom: activeTab === 'available' ? 2 : 0,
              borderColor: '#2563eb',
              borderRadius: 0,
              pb: 1.5,
              mr: 3,
              fontWeight: activeTab === 'available' ? 600 : 400,
            }}
          >
            Available Coupons ({availableCoupons.length})
          </Button>
          <Button
            onClick={() => setActiveTab('my-coupons')}
            sx={{
              textTransform: 'none',
              color: activeTab === 'my-coupons' ? '#2563eb' : '#6b7280',
              borderBottom: activeTab === 'my-coupons' ? 2 : 0,
              borderColor: '#2563eb',
              borderRadius: 0,
              pb: 1.5,
              fontWeight: activeTab === 'my-coupons' ? 600 : 400,
            }}
          >
            My Coupons ({userCoupons.length})
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {activeTab === 'available' && (
              <>
                {availableCoupons.length === 0 ? (
                  <Box textAlign="center" py={8}>
                    <Typography variant="h6" color="textSecondary" mb={2}>
                      No coupons available at the moment
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Check back later for exciting offers!
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    {availableCoupons.map(coupon => (
                      <Grid item xs={12} sm={6} md={4} key={coupon.id}>
                        {renderCouponCard(coupon, isCouponClipped(coupon.id))}
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}

            {activeTab === 'my-coupons' && (
              <>
                {!isAuthenticated ? (
                  <Box textAlign="center" py={8}>
                    <Typography variant="h6" color="textSecondary" mb={2}>
                      Please log in to view your coupons
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/login', { state: { returnTo: '/coupons' } })}
                      sx={{ textTransform: 'none' }}
                    >
                      Log In
                    </Button>
                  </Box>
                ) : userCoupons.length === 0 ? (
                  <Box textAlign="center" py={8}>
                    <Typography variant="h6" color="textSecondary" mb={2}>
                      You haven't clipped any coupons yet
                    </Typography>
                    <Typography variant="body2" color="textSecondary" mb={3}>
                      Browse available coupons and clip them to use during checkout!
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setActiveTab('available')}
                      sx={{ textTransform: 'none' }}
                    >
                      View Available Coupons
                    </Button>
                  </Box>
                ) : (
                  <Grid container spacing={3}>
                    {userCoupons
                      .filter(userCoupon => userCoupon.coupon) // Filter out coupons with missing data
                      .map(userCoupon => (
                        <Grid item xs={12} sm={6} md={4} key={userCoupon.id}>
                          {renderCouponCard(userCoupon.coupon, true)}
                        </Grid>
                      ))}
                  </Grid>
                )}
              </>
            )}
          </>
        )}
      </Container>
    </div>
  );
};

export default CouponsPage;

